import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { useRef } from "react";
import { Terminal } from "lucide-react";
import { useIsMobile } from "./ui/use-mobile";
import { Tooltip, TooltipTrigger, TooltipContent } from "./ui/tooltip";

type SkillIconConfig = {
  title: string;
  cls?: string;
  src?: string;
};

// Icon sets
const LANGUAGE_ICONS: SkillIconConfig[] = [
  { title: "Java", cls: "devicon-java-plain" },
  { title: "Python", cls: "devicon-python-plain" },
  { title: "JavaScript", cls: "devicon-javascript-plain" },
  { title: "C++", cls: "devicon-cplusplus-plain" },
  { title: "C", cls: "devicon-c-plain" },
  { title: "HTML", cls: "devicon-html5-plain-wordmark" },
  { title: "CSS", cls: "devicon-css3-plain-wordmark" },
  { title: "SQL", cls: "devicon-sqlite-plain" },
  { title: "TypeScript", cls: "devicon-typescript-plain" },
  { title: "YAML", cls: "devicon-yaml-plain" },
  { title: "R", cls: "devicon-r-plain" },
  { title: "Bash", cls: "devicon-bash-plain" },
];
const TOOL_ICONS: SkillIconConfig[] = [
  { title: "Google Cloud", cls: "devicon-googlecloud-plain" },
  { title: "MongoDB", cls: "devicon-mongodb-plain" },
  { title: "Figma", cls: "devicon-figma-plain" },
  { title: "Azure", cls: "devicon-azure-plain" },
  { title: "Jira", cls: "devicon-jira-plain" },
  { title: "Cypress", cls: "devicon-cypressio-plain" },
  { title: "Postman", cls: "devicon-postman-plain" },
  { title: "PostgreSQL", cls: "devicon-postgresql-plain" },
  { title: "Firebase", cls: "devicon-firebase-plain" },
  { title: "TensorFlow", cls: "devicon-tensorflow-original" },
  { title: "AWS", cls: "devicon-amazonwebservices-plain" },
  { title: "Kubernetes", cls: "devicon-kubernetes-plain" },
  { title: "Terraform", cls: "devicon-terraform-plain" },
  { title: "Cloud SQL", src: "/cloudsql.svg" },
  { title: "OpenTelemetry", cls: "devicon-opentelemetry-plain" },
  { title: "Datadog", cls: "devicon-datadog-original" },
];
const TECH_ICONS: SkillIconConfig[] = [
  { title: "Node.js", cls: "devicon-nodejs-plain" },
  { title: "Flutter", cls: "devicon-flutter-plain" },
  { title: "React.js", cls: "devicon-react-original-wordmark" },
  { title: "React Native", cls: "devicon-react-original" },
  { title: "Next.js", cls: "devicon-nextjs-plain" },
  { title: "Flask", cls: "devicon-flask-original" },
  { title: "Spring Boot", cls: "devicon-spring-plain" },
  { title: "Docker", cls: "devicon-docker-plain" },
  { title: "Git", cls: "devicon-git-plain" },
  { title: "Supabase", src: "/supabase.svg" },
  { title: "Redis", cls: "devicon-redis-plain" },
  { title: "Vercel", src: "/vercel.svg" },
  { title: "FastAPI", cls: "devicon-fastapi-plain" },
  { title: "LiteLLM", src: "/litellm.svg" },
  { title: "Claude", src: "/claude.svg" },
  { title: "OpenAI", src: "/openai.svg" },
  
];

const SKILL_CATEGORIES = [
  { title: "Languages", icons: LANGUAGE_ICONS, color: "from-blue-500/20 to-cyan-500/20" },
  { title: "Tools", icons: TOOL_ICONS, color: "from-purple-500/20 to-pink-500/20" },
  { title: "Technologies", icons: TECH_ICONS, color: "from-green-500/20 to-emerald-500/20" },
];

export function SkillsSection() {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: wrapperRef,
    offset: ["start start", "end start"],
  });
  const prefersReduced = useReducedMotion();
  const isMobile = useIsMobile();
  const factor = prefersReduced || isMobile ? 0.5 : 1;

  const scale = useTransform(scrollYProgress, [0, 1], [1, 1 - 0.1 * factor]);
  const opacity = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    [1, 0.5, 0],
  );

  // Title parallax
  const titleY = useTransform(
    scrollYProgress,
    [0, 1],
    [0, -120 * factor],
  );

  return (
    <div ref={wrapperRef} className="relative">
      <section
        className="min-h-screen sticky top-0 pt-16 sm:pt-24 md:pt-32 lg:pt-40 pb-6 sm:pb-8 md:pb-10 px-4 sm:px-6 md:px-8 lg:px-12 bg-black text-[#eceff4] overflow-hidden industrial-grid"
        style={{ zIndex: 4 }}
      >
        <div className="scanline" />

        <motion.div
          style={{ scale, opacity }}
          className="max-w-7xl mx-auto relative z-10"
        >
          {/* Section label */}
          <div className="absolute -top-12 sm:-top-16 md:-top-20 left-0 flex items-center gap-4">
            <Terminal className="w-4 h-4 text-[#d8dee9]/20" />
            <span className="mono text-xs tracking-[0.4em] text-[#d8dee9]/20">
              SECTION_03
            </span>
          </div>

          {/* Title */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{
              duration: 1,
              ease: [0.25, 0.1, 0.25, 1],
            }}
            viewport={{ once: true, margin: "-100px" }}
            className="mb-12 sm:mb-16 md:mb-20"
            style={{ y: titleY }}
          >
            <h2
              className="text-[4rem] sm:text-[5rem] md:text-[6rem] lg:text-[8rem] xl:text-[12rem] leading-[0.85] grotesk tracking-tighter"
              style={{
                fontFamily: "var(--font-grotesk)",
                fontWeight: 700,
              }}
            >
              Skills
            </h2>
            <div className="flex items-center gap-4 mt-4">
              <div className="w-24 h-px bg-[#eceff4]" />
              <span className="mono text-xs text-[#d8dee9]/40">
                TECHNICAL EXPERTISE
              </span>
            </div>
          </motion.div>

          {/* Skills Grid - Hybrid Design */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10">
            {SKILL_CATEGORIES.map((category, categoryIndex) => (
              <SkillCategory
                key={category.title}
                category={category}
                index={categoryIndex}
              />
            ))}
          </div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.5 }}
            viewport={{ once: true }}
            className="mt-20 sm:mt-28 md:mt-32 lg:mt-40 pt-12 sm:pt-16 md:pt-20 border-t border-[#d8dee9]/20"
          >
            <div className="flex justify-center">
              <div className="mono text-xs sm:text-sm text-[#eceff4]/80 tracking-wider text-center">
                © 2025 - KATHAN SHETH
                <br />
                ALL RIGHTS RESERVED.
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Background decorative elements with depth parallax */}
        <motion.div
          style={{
            y: useTransform(scrollYProgress, [0, 1], [0, -250 * factor]),
            x: useTransform(scrollYProgress, [0, 1], [0, 150 * factor]),
            scale: useTransform(scrollYProgress, [0, 1], [1, 1.3]),
            opacity: useTransform(scrollYProgress, [0, 1], [0.15, 0]),
          }}
          className="absolute top-1/4 right-0 w-[600px] h-[600px] border border-[#d8dee9]/5 -translate-y-1/2 translate-x-1/2 pointer-events-none z-0"
        />
        <motion.div
          style={{
            y: useTransform(scrollYProgress, [0, 1], [0, 250 * factor]),
            x: useTransform(scrollYProgress, [0, 1], [0, -150 * factor]),
            scale: useTransform(scrollYProgress, [0, 1], [1, 1.2]),
            opacity: useTransform(scrollYProgress, [0, 1], [0.15, 0]),
          }}
          className="absolute bottom-1/4 left-0 w-[500px] h-[500px] border border-[#d8dee9]/5 translate-y-1/2 -translate-x-1/2 pointer-events-none z-0"
        />
        {/* Additional depth layers */}
        <motion.div
          style={{
            y: useTransform(scrollYProgress, [0, 1], [0, -180 * factor]),
            x: useTransform(scrollYProgress, [0, 1], [0, 120 * factor]),
            scale: useTransform(scrollYProgress, [0, 1], [0.8, 1.1]),
            opacity: useTransform(scrollYProgress, [0, 1], [0.1, 0]),
          }}
          className="absolute top-1/2 left-1/2 w-[400px] h-[400px] border border-[#d8dee9]/3 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-0"
        />
        <motion.div
          style={{
            y: useTransform(scrollYProgress, [0, 1], [0, 180 * factor]),
            x: useTransform(scrollYProgress, [0, 1], [0, -120 * factor]),
            scale: useTransform(scrollYProgress, [0, 1], [0.7, 1.0]),
            opacity: useTransform(scrollYProgress, [0, 1], [0.08, 0]),
          }}
          className="absolute top-1/2 left-1/2 w-[300px] h-[300px] border border-[#d8dee9]/3 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-0"
        />
      </section>
    </div>
  );
}

function SkillCategory({
  category,
  index,
}: {
  category: { title: string; icons: SkillIconConfig[]; color: string };
  index: number;
}) {
  const categoryRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: categoryRef,
    offset: ["start end", "end start"],
  });
  const prefersReduced = useReducedMotion();
  const isMobile = useIsMobile();
  const factor = prefersReduced || isMobile ? 0.5 : 1;

  // Depth-based parallax - each category moves at different speeds
  const depthMultiplier = [0.6, 1.0, 1.4][index] || 1.0; // Different depths for each column
  const y = useTransform(
    scrollYProgress,
    [0, 1],
    [100 * factor * depthMultiplier, -100 * factor * depthMultiplier]
  );
  const x = useTransform(
    scrollYProgress,
    [0, 1],
    [(index - 1) * 40 * factor, (index - 1) * -40 * factor] // Sideways movement for depth
  );
  const scale = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    [0.92 + (index * 0.03), 1, 0.96 - (index * 0.02)] // Scale variation for depth
  );
  const opacity = useTransform(scrollYProgress, [0, 0.3, 0.7, 1], [0, 1, 1, 0]);

  return (
    <motion.div
      ref={categoryRef}
      style={{ y, x, scale, opacity }}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: index * 0.15 }}
      viewport={{ once: true, margin: "-100px" }}
      className="relative p-6 lg:p-8"
    >
      {/* Category Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: index * 0.15 + 0.2 }}
        viewport={{ once: true }}
        className="mb-6"
      >
        <motion.h3
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: index * 0.15 + 0.3 }}
          viewport={{ once: true }}
          className="mono text-xl lg:text-2xl tracking-[0.2em] text-[#eceff4] font-bold mb-3"
        >
          {category.title}
        </motion.h3>
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: "100%" }}
          transition={{ duration: 0.8, delay: index * 0.15 + 0.4 }}
          viewport={{ once: true }}
          className="h-px bg-gradient-to-r from-white/40 via-white/20 to-transparent"
        />
      </motion.div>

      {/* Icons Grid */}
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 lg:gap-4">
        {category.icons.map((icon, iconIndex) => (
          <SkillIcon
            key={icon.title}
            icon={icon}
            index={iconIndex}
            categoryIndex={index}
          />
        ))}
      </div>
    </motion.div>
  );
}

function SkillIcon({
  icon,
  index,
  categoryIndex,
}: {
  icon: SkillIconConfig;
  index: number;
  categoryIndex: number;
}) {
  const iconRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: iconRef,
    offset: ["start end", "end start"],
  });
  const prefersReduced = useReducedMotion();
  const isMobile = useIsMobile();
  const factor = prefersReduced || isMobile ? 0.3 : 1;

  // Individual icon parallax - creates depth within each category
  const iconY = useTransform(
    scrollYProgress,
    [0, 1],
    [
      index % 3 === 0 ? 25 * factor : index % 3 === 1 ? -20 * factor : 15 * factor,
      index % 3 === 0 ? -25 * factor : index % 3 === 1 ? 20 * factor : -15 * factor
    ]
  );
  const iconX = useTransform(
    scrollYProgress,
    [0, 1],
    [
      index % 4 === 0 ? 10 * factor : index % 4 === 2 ? -10 * factor : 0,
      index % 4 === 0 ? -10 * factor : index % 4 === 2 ? 10 * factor : 0
    ]
  );
  const iconScale = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    [0.88 + (index % 5) * 0.025, 1, 0.94 - (index % 5) * 0.015]
  );
  const iconOpacity = useTransform(
    scrollYProgress,
    [0, 0.2, 0.8, 1],
    [0.7 + (index % 3) * 0.1, 1, 1, 0.8 - (index % 3) * 0.1]
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <motion.div
          ref={iconRef}
          style={{ y: iconY, x: iconX, scale: iconScale, opacity: iconOpacity }}
          initial={{ opacity: 0, scale: 0.6, y: 20 }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }}
          transition={{
            duration: 0.5,
            delay: categoryIndex * 0.2 + index * 0.03,
            ease: [0.25, 0.1, 0.25, 1],
          }}
          viewport={{ once: true, margin: "-50px" }}
          whileHover={{ scale: 1.15, y: -5, z: 10 }}
          className="group relative cursor-pointer"
        >
          <div className="relative aspect-square bg-gradient-to-br from-[#d8dee9]/5 to-[#d8dee9]/0 border border-[#d8dee9]/10 hover:border-[#e5e9f0]/30 transition-all duration-300 flex items-center justify-center p-2 overflow-hidden">
            {/* Hover glow effect */}
            <motion.div
              className="absolute inset-0 bg-gradient-to-br from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            />
            
            {/* Icon - perfectly centered */}
            {icon.cls ? (
              <i
                className={`${icon.cls} text-3xl lg:text-4xl absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 transition-all duration-300 group-hover:scale-110`}
                title={icon.title}
              />
            ) : icon.src ? (
              <img
                src={icon.src}
                alt={icon.title}
                loading="lazy"
                className="w-10 h-10 lg:w-12 lg:h-12 object-contain absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 transition-all duration-300 group-hover:scale-110 filter invert brightness-[1.2] saturate-0"
              />
            ) : null}

            {/* Corner accents */}
            <div className="absolute top-0 left-0 w-2 h-2 border-t border-l border-[#d8dee9]/20 group-hover:border-[#eceff4]/60 transition-colors" />
            <div className="absolute top-0 right-0 w-2 h-2 border-t border-r border-[#d8dee9]/20 group-hover:border-[#eceff4]/60 transition-colors" />
            <div className="absolute bottom-0 left-0 w-2 h-2 border-b border-l border-[#d8dee9]/20 group-hover:border-[#eceff4]/60 transition-colors" />
            <div className="absolute bottom-0 right-0 w-2 h-2 border-b border-r border-[#d8dee9]/20 group-hover:border-[#eceff4]/60 transition-colors" />
          </div>
        </motion.div>
      </TooltipTrigger>
      <TooltipContent
        side="right"
        sideOffset={8}
        className="relative bg-transparent border-none p-0 [&>svg]:hidden"
      >
        <motion.div
          initial={{ opacity: 0, clipPath: "inset(0 100% 0 0)" }}
          animate={{ opacity: 1, clipPath: "inset(0 0% 0 0)" }}
          exit={{ opacity: 0, clipPath: "inset(0 100% 0 0)" }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="relative"
        >
          <div className="relative bg-black px-3 py-1.5">
            {/* Pulsating left border */}
            <motion.div
              className="absolute left-0 top-0 bottom-0 w-[2px] bg-[#eceff4]/40"
              animate={{
                opacity: [0.4, 0.8, 0.4],
                backgroundColor: [
                  "rgba(255, 255, 255, 0.4)",
                  "rgba(255, 255, 255, 0.8)",
                  "rgba(255, 255, 255, 0.4)",
                ],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
            
            {/* Text */}
            <p className="mono text-[10px] text-[#eceff4]/80 tracking-[0.2em] uppercase font-medium">
              {icon.title}
            </p>
          </div>
        </motion.div>
      </TooltipContent>
    </Tooltip>
  );
}